import { supabase } from '@/integrations/supabase/client';

export interface UserWorkspace {
  id: string;
  name: string;
  slug: string;
  plan_type?: string;
  domain?: string;
  domain_verified?: boolean;
  role: 'workspace_admin' | 'workspace_member';
  status: 'active' | 'pending' | 'suspended';
  is_default: boolean;
  member_count?: number;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceMembership {
  id: string;
  workspace_id: string;
  user_id: string;
  role: 'workspace_admin' | 'workspace_member';
  invited_by?: string;
  invited_at?: string;
  joined_at?: string;
  status?: string;
  created_at: string;
  updated_at: string;
}

export class WorkspaceService {
  /**
   * Get all workspaces for the current user
   * Simplified version to avoid TypeScript issues
   */
  static async getUserWorkspaces(): Promise<UserWorkspace[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return [];
      }

      // Get user's workspace memberships
      const { data: memberships, error } = await (supabase as any)
        .from('workspace_memberships')
        .select('workspace_id, role, status, is_default')
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (error || !memberships) {
        console.error('❌ WorkspaceService.getUserWorkspaces: Error getting memberships:', error);
        return [];
      }

      // Get workspace details and member counts in parallel
      const workspaces: UserWorkspace[] = [];
      await Promise.all(memberships.map(async (membership: any) => {
        const { data: workspace } = await (supabase as any)
          .from('watt_workspaces')
          .select('id, name, slug, created_at, updated_at')
          .eq('id', membership.workspace_id)
          .single();

        if (workspace) {
          // Fetch member count for this workspace
          const { count: memberCount } = await (supabase as any)
            .from('workspace_memberships')
            .select('*', { count: 'exact', head: true })
            .eq('workspace_id', workspace.id)
            .eq('status', 'active');

          workspaces.push({
            id: workspace.id,
            name: workspace.name,
            slug: workspace.slug,
            role: membership.role,
            status: membership.status,
            is_default: membership.is_default || false,
            member_count: memberCount || 1,
            created_at: workspace.created_at,
            updated_at: workspace.updated_at,
          });
        }
      }));

      const sortedWorkspaces = workspaces.sort((a, b) => {
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      return sortedWorkspaces;
    } catch (error) {
      console.error('Error fetching user workspaces:', error);
      return [];
    }
  }

  /**
   * Get the user's default workspace
   */
  static async getDefaultWorkspace(): Promise<UserWorkspace | null> {
    try {
      const workspaces = await this.getUserWorkspaces();
      
      // Return explicitly marked default workspace
      const defaultWorkspace = workspaces.find(w => w.is_default);
      if (defaultWorkspace) return defaultWorkspace;
      
      // If no explicit default and user has only one workspace, that's the default
      if (workspaces.length === 1) {
        return workspaces[0];
      }
      
      return null;
    } catch (error) {
      console.error('Error getting default workspace:', error);
      return null;
    }
  }

  /**
   * Set a workspace as the user's default
   */
  static async setDefaultWorkspace(workspaceId: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      // Clear all existing defaults for this user
      await (supabase as any)
        .from('workspace_memberships')
        .update({ is_default: false })
        .eq('user_id', user.id);

      // Set the new default
      const { error } = await (supabase as any)
        .from('workspace_memberships')
        .update({ is_default: true })
        .eq('user_id', user.id)
        .eq('workspace_id', workspaceId);

      return !error;
    } catch (error) {
      console.error('Error setting default workspace:', error);
      return false;
    }
  }

  /**
   * Create a new workspace
   */
  static async createWorkspace(name: string, slug: string): Promise<UserWorkspace | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Create the workspace
      const { data: workspace, error: workspaceError } = await (supabase as any)
        .from('watt_workspaces')
        .insert([{
          name,
          slug,
          domain: null
        }])
        .select()
        .single();

      if (workspaceError) throw workspaceError;

      // Create workspace membership for the user as admin
      const { error: membershipError } = await (supabase as any)
        .from('workspace_memberships')
        .insert([{
          workspace_id: workspace.id,
          user_id: user.id,
          role: 'workspace_admin',
          status: 'active',
          is_default: true // New workspace becomes default
        }]);

      if (membershipError) throw membershipError;

      return {
        id: workspace.id,
        name: workspace.name,
        slug: workspace.slug,
        role: 'workspace_admin',
        status: 'active',
        is_default: true,
        member_count: 1,
        created_at: workspace.created_at,
        updated_at: workspace.updated_at,
      };
    } catch (error) {
      console.error('Error creating workspace:', error);
      return null;
    }
  }

  /**
   * Check if a workspace slug is available
   */
  static async isSlugAvailable(slug: string): Promise<boolean> {
    try {
      const { data } = await (supabase as any)
        .from('watt_workspaces')
        .select('id')
        .eq('slug', slug)
        .single();

      return !data;
    } catch (error) {
      return true;
    }
  }

  /**
   * Get workspace by slug
   */
  static async getWorkspaceBySlug(slug: string): Promise<UserWorkspace | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // First, get the workspace by slug (this should work with our RLS policies)
      const { data: workspace, error: workspaceError } = await (supabase as any)
        .from('watt_workspaces')
        .select('id, name, slug, plan_type, domain, domain_verified, created_at, updated_at')
        .eq('slug', slug)
        .single();

      if (workspaceError) {
        if (workspaceError.code === 'PGRST116') {
          return null;
        }
        throw workspaceError;
      }

      if (!workspace) return null;

      // Second, get the user's membership for this workspace
      const { data: membership, error: membershipError } = await (supabase as any)
        .from('workspace_memberships')
        .select('id, role, status, is_default')
        .eq('user_id', user.id)
        .eq('workspace_id', workspace.id)
        .eq('status', 'active')
        .single();

      if (membershipError) {
        if (membershipError.code === 'PGRST116') {
          return null;
        }
        throw membershipError;
      }

      if (!membership) return null;

      return {
        id: workspace.id,
        name: workspace.name,
        slug: workspace.slug,
        plan_type: workspace.plan_type,
        domain: workspace.domain,
        domain_verified: workspace.domain_verified,
        role: membership.role,
        status: membership.status,
        is_default: membership.is_default || false,
        member_count: 1,
        created_at: workspace.created_at,
        updated_at: workspace.updated_at,
      };
    } catch (error) {
      console.error('Error getting workspace by slug:', error);
      return null;
    }
  }
} 